import { type CSSProperties, useMemo } from 'react';
import { encode } from 'uqr';

import type { PhysicalCardPrintRow } from '../api.js';

/**
 * One card face in the approved layout «د، قاب لوکس» (D-119, AC §33.8): frame, brand, the number on
 * four lines, the Persian name, the QR of `qrText`, the detail line, and the tagline. The markup is
 * the same on paper and on screen; `card-print.css` sets the unit and the colours. The printed strings
 * are the server's: nothing here re-groups, re-pads, or recomputes them.
 */

export type CardFaceRow = Pick<
  PhysicalCardPrintRow,
  'cardNumberGrouped' | 'citySerial' | 'cvv2' | 'expiryFull' | 'holderNameFa' | 'qrText'
>;

/** The lines under the QR; a pre-D-117 number has no serial line (Q-D119-05). */
export function cardDetailLines(row: CardFaceRow): readonly string[] {
  const lines = [`EXP ${row.expiryFull}`, `CVV2 ${row.cvv2}`];
  if (row.citySerial !== null) lines.push(row.citySerial);
  return lines;
}

/**
 * The module size for a QR: whole printer dots (one dot is 0.8467 layout units), as large as the
 * layout allows. A long `qrLinkBase` makes more modules, so the modules get smaller to stay in frame.
 */
export function qrModuleUnits(modules: number): number {
  const dots = modules <= 45 ? 6 : modules <= 53 ? 5 : 4;
  return dots * 0.84667;
}

/** The QR of the exact text, ECC M with a four-module quiet zone; the card's own colour is the ink. */
function CardQr({ text }: { readonly text: string }) {
  const { path, size } = useMemo(() => {
    const qr = encode(text, { border: 4, ecc: 'M' });
    let d = '';
    qr.data.forEach((row, y) => {
      row.forEach((dark, x) => {
        if (dark) d += `M${x} ${y}h1v1h-1z`;
      });
    });
    return { path: d, size: qr.size };
  }, [text]);
  return (
    <svg
      aria-hidden="true"
      className="saay-cr80__qr"
      shapeRendering="crispEdges"
      style={
        {
          '--saay-cr80-qr-module': qrModuleUnits(size),
          '--saay-cr80-qr-modules': size,
        } as CSSProperties
      }
      viewBox={`0 0 ${size} ${size}`}
    >
      <path d={path} fill="currentColor" />
    </svg>
  );
}

export function CardFace({
  mode,
  row,
}: {
  readonly mode: 'paper' | 'screen';
  readonly row: CardFaceRow;
}) {
  return (
    <div className={`saay-cr80 saay-cr80--${mode}`}>
      <div className="saay-cr80__stripe" />
      <div className="saay-cr80__frame" />
      <div className="saay-cr80__body">
        <div className="saay-cr80__brand" dir="ltr">
          SARAY
        </div>
        <div className="saay-cr80__brand-line" dir="ltr">
          CIRCLE
        </div>
        <div className="saay-cr80__number" dir="ltr">
          {row.cardNumberGrouped.split(' ').map((group, index) => (
            <div key={index}>{group}</div>
          ))}
        </div>
        <div className="saay-cr80__holder" dir="rtl">
          {row.holderNameFa}
        </div>
        <CardQr text={row.qrText} />
        <div className="saay-cr80__detail" dir="ltr">
          {cardDetailLines(row).map((line) => (
            <div key={line}>{line}</div>
          ))}
        </div>
        <div className="saay-cr80__tagline" dir="ltr">
          Create. Do It.
        </div>
      </div>
    </div>
  );
}
